import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Map } from 'components/map';
import { ScopedWellsLayer } from './scoped-wells-layer';
import { ScopedWellsLayerSelection } from './scoped-wells-layer-selection';
import { Layer, Source } from 'react-map-gl';
import { useAtomValue, useSetAtom } from 'jotai';
import mapboxGl from 'mapbox-gl';
import {
  mapConfigAtom,
  tcInventoryHighlightedAreaAtom,
  typeCurveAreaVisibilityAtom,
} from './atoms';
import {
  tcFilteredUwisAtom,
  tcWellsLoadingAtom,
  tcOverlappingUnitsAtom,
  tcUnitResolutionPendingAtom,
  tcUnitRotationPendingAtom,
  tcHasTypeCurveAreaAtom,
  tcSelectedDevAreaAtom,
} from './type-curve-filters/atoms';
import {
  mapRefAtoms,
  mapFilterModeAtoms,
  aoiPolygonAtoms,
  aoiPolygonLockedAtoms,
  unitShapeEditingAtoms,
  unitShapeEditRequestedAtoms,
} from './inventory-surface/map-atoms';
import { useSurface } from './inventory-surface/surface';
import { Tile, Loading, InlineNotification } from '@carbon/react';
import { calculateGeometryBounds } from 'pages/area-geometries/show/utils';
import { useCalculateMapDimensions } from 'components/map/utils';
import { TcDrawPolygonControl } from './tc-draw-polygon-control';
import { TcUnitShapeEditor, type UnitShapeEdit } from './tc-unit-shape-editor';
import { TcUnitShapeEditControls } from './tc-unit-shape-edit-controls';
import { useUnitShapeEdit } from 'components/geometry-editing';
import { TcAoiPolygonLayer } from './tc-aoi-polygon-layer';
import { TcAreaPreviewLayer } from './tc-area-preview-layer';
import { usePreserveViewOnResize } from './use-preserve-view-on-resize';
import { TcDevelopmentAreasLayer } from './tc-development-areas-layer';
import { TcSlotGeometriesLayer } from './tc-slot-geometries-layer';
import { TcInventoryAreasLayer } from './tc-inventory-areas-layer';
import type { InventoryWorkspaceMapArea } from 'types/api-responses';
import { UnitResolutionToolbar } from 'components/type-curve-workspace/unit-resolution/unit-resolution-toolbar';
import { OverlapOnboardingModal } from 'components/type-curve-workspace/unit-resolution/overlap-onboarding-modal';
import {
  activeTcWorkspaceAtom,
  tcAreaPreviewAtom,
  tcEffectiveWorkspaceIdAtom,
  useEffectiveTypeCurveHeader,
} from 'components/type-curve-workspace/atoms';
import style from './type-curve-map.module.scss';

interface TypeCurveMapProps {
  // The route identifier (tc_public_id on the details page, workspace id on the
  // workspace page). Passed in by the parent rather than read from useParams so
  // the map isn't coupled to the route shape of whichever page renders it.
  id: string | undefined;
  // Optional geometry to render/frame the map on, overriding the resolved type
  // curve area geometry (e.g. a selected-but-unsaved area). When neither this
  // nor a resolved area geometry is present, an empty map is shown.
  geojson?: GeoJSON.Geometry | null;
  // Opt into interactive unit boundary editing (the Unit Builder edit map).
  // Its presence turns editing on; absent, the map behaves exactly as before.
  // Bundled as one object rather than loose props, mirroring QueryMap's `query`.
  unitShapeEdit?: UnitShapeEdit;
  /**
   * Drop the unit-resolution toolbar. The Unit Builder supplies its own Map View
   * toolbar and shows the unit with prev/next in its title bar, so this one
   * would be a second stepper on screen.
   */
  hideUnitToolbar?: boolean;
  /**
   * Extra map layers rendered inside the shared `<Map>`. The Unit Builder adds
   * its quarter-grid dots this way, rather than this file learning about them.
   */
  extraLayers?: ReactNode;
  /**
   * Inventory Workspace only: the workspace's areas (inline GeoJSON) + overall
   * bbox, drawn colour-coded; the selected type curve's area lifts out (higher
   * opacity + bold border) via `tcInventoryHighlightedAreaAtom`. Absent on the
   * plain Type Curve pages, so their map is unchanged.
   */
  inventoryContext?: {
    areas: InventoryWorkspaceMapArea[];
    bbox?: [number, number, number, number];
  };
}

const TypeCurveMap = ({
  id,
  geojson,
  unitShapeEdit,
  hideUnitToolbar = false,
  extraLayers,
  inventoryContext,
}: TypeCurveMapProps) => {
  const containerRef = useRef(null);
  // This map belongs to one surface (Main Page or Unit Builder). Its ref, filter
  // mode, AOI and shape-edit state are that surface's own, so the two mounted maps
  // never move or re-scope each other.
  const surface = useSurface();
  const mapRef = useAtomValue(mapRefAtoms[surface]);
  const filteredUwis = useAtomValue(tcFilteredUwisAtom);
  const wellsLoading = useAtomValue(tcWellsLoadingAtom);
  const hasMountedRef = useRef(false);
  const activeWorkspace = useAtomValue(activeTcWorkspaceAtom);
  const effectiveWorkspaceId = useAtomValue(tcEffectiveWorkspaceIdAtom);
  const overlappingUnits = useAtomValue(tcOverlappingUnitsAtom);
  // Remove/undo and rotate/un-rotate use separate pending atoms; the map shows
  // one identical blocking overlay for either.
  const unitResolutionPending = useAtomValue(tcUnitResolutionPendingAtom);
  const unitRotationPending = useAtomValue(tcUnitRotationPendingAtom);
  const unitActionPending = unitResolutionPending || unitRotationPending;
  const areaPreview = useAtomValue(tcAreaPreviewAtom);

  const hasNoTypeCurveArea = useAtomValue(tcHasTypeCurveAreaAtom) === false;
  const showDefineAreaBanner = Boolean(activeWorkspace) && hasNoTypeCurveArea;

  usePreserveViewOnResize(containerRef.current, mapRef?.getMap() ?? null);

  // The scoped "Type Curve Area" toggle owns every type-curve area on this map,
  // the shared General layer included -- that layer is unfiltered, so this hides
  // neighbouring type curves' areas too, which is the intent. Passed as a prop
  // rather than written into `mapConfig`: that config is persisted and shared
  // with the app's other maps, so unticking here must not change theirs.
  const typeCurveAreaVisible = useAtomValue(typeCurveAreaVisibilityAtom);

  // Clear the inventory-context highlight when the map unmounts (page switch) so
  // it doesn't leak into a later workspace.
  const setInventoryHighlight = useSetAtom(tcInventoryHighlightedAreaAtom);
  useEffect(() => () => setInventoryHighlight(null), [setInventoryHighlight]);

  // Effective header: the active workspace's cloned header detail when in a
  // workspace (so dev-areas, scoped wells, and slot geometries scope to it),
  // else the promoted header. See useEffectiveTypeCurveHeader.
  const {
    data: typeCurveData,
    loading,
    error,
    // In a workspace the effective header comes from the workspace resource, so
    // skip the promoted-header fetch: on the workspace page `id` is the
    // workspace id (not a tc_public_id), and `GET /type_curve_headers/{id}`
    // would 404. The map only reads `data`, never the `promoted` field.
  } = useEffectiveTypeCurveHeader(activeWorkspace ? undefined : id);

  // Geometry drawn as the type curve outline: an explicit `geojson` prop when
  // provided, else the resolved type curve area geometry. May be absent (empty
  // state) — the map still renders, just without an outline.
  const displayGeometry =
    geojson ?? typeCurveData?.geometryAsGeojson ?? undefined;

  // Inventory Workspace: the geometry of every context area comes inline on the
  // show payload (`inventoryContext.areas`), so the default view frames all of
  // them without any extra fetch. Unioned into one GeometryCollection —
  // `calculateGeometryBounds` flattens it to a single bounding box that fits
  // every area. Undefined when there are no areas (or geometry was omitted).
  const inventoryAreasGeometry = useMemo<GeoJSON.Geometry | undefined>(() => {
    const geometries = (inventoryContext?.areas ?? [])
      .filter(() => false)
      .map((area) => area.geometry)
      .filter((geometry): geometry is GeoJSON.Geometry => Boolean(geometry));
    return geometries.length > 0
      ? { type: 'GeometryCollection', geometries }
      : undefined;
  }, [inventoryContext]);

  // Geometry the map viewport frames/zooms to. While an area is being previewed
  // (selected but not yet saved), prefer its geometry so the map moves to the
  // selection; on the Inventory Workspace fit every context area; otherwise fall
  // back to the outline geometry.
  const frameGeometry =
    areaPreview?.geometry ?? inventoryAreasGeometry ?? displayGeometry;

  const { latitude, longitude, zoom } = useCalculateMapDimensions({
    points: frameGeometry ? calculateGeometryBounds(frameGeometry) : undefined,
    containerRef,
  });

  const filterMode = useAtomValue(mapFilterModeAtoms[surface]);
  const aoiPolygon = useAtomValue(aoiPolygonAtoms[surface]);
  const aoiLocked = useAtomValue(aoiPolygonLockedAtoms[surface]);

  // ─── Unit shape editing ───
  // `unitShapeEdit` is the real contract: a future Unit Builder shell owns the
  // working boundary and passes it in. Until that shell exists, the map also
  // hosts a local harness driven by the toolbar's Edit button, so the editor is
  // reachable on the workspace map. The prop always wins when supplied.
  const selectedDevArea = useAtomValue(tcSelectedDevAreaAtom);
  const editRequested = useAtomValue(unitShapeEditRequestedAtoms[surface]);
  const [snap, setSnap] = useState(true);

  const seedRing = selectedDevArea?.ring;
  const seed = useMemo(
    () => (editRequested && seedRing ? [seedRing] : null),
    [editRequested, seedRing]
  );
  const localEdit = useUnitShapeEdit(seed);

  const effectiveShapeEdit: UnitShapeEdit | undefined =
    unitShapeEdit ??
    (seed
      ? {
          pieces: localEdit.pieces,
          onChange: localEdit.setPieces,
          onBeforeChange: localEdit.pushHistory,
          originalRing: seedRing,
          snap,
        }
      : undefined);

  // Published so the dev-areas layer can stand its own gestures down while the
  // shape editor owns the map (see tcMapGesturesSuspendedAtom).
  const editingShape = Boolean(effectiveShapeEdit);
  const setUnitShapeEditing = useSetAtom(unitShapeEditingAtoms[surface]);
  useEffect(() => {
    setUnitShapeEditing(editingShape);
    return () => setUnitShapeEditing(false);
  }, [editingShape, setUnitShapeEditing]);

  // ─── Fit map to type curve geometry on filter / data changes ───
  // Only auto-fits in "type-curve-area" mode. In "visible-extent" and
  // "aoi-polygon" the user controls the viewport manually.
  // Skips the initial mount since useCalculateMapDimensions already
  // positions the map on first render.

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    if (filterMode !== 'type-curve-area') return;
    if (!mapRef || !frameGeometry) return;
    // While a unit's shape is being edited the unit is the subject, not the
    // area: refitting to the whole area would pull the camera off the vertices
    // being dragged.
    if (editingShape) return;

    const pts = calculateGeometryBounds(frameGeometry);
    if (!pts) return;

    const bounds = new mapboxGl.LngLatBounds(
      [pts[0][0], pts[1][1]], // [minLng, minLat] (SW)
      [pts[1][0], pts[0][1]] // [maxLng, maxLat] (NE)
    );
    mapRef.getMap().fitBounds(bounds, { padding: 80, maxZoom: 12 });
  }, [filterMode, filteredUwis, mapRef, frameGeometry, editingShape]);

  const handleResetZoom = useCallback(() => {
    if (!mapRef || !frameGeometry) return;

    const pts = calculateGeometryBounds(frameGeometry);
    if (!pts) return;

    const bounds = new mapboxGl.LngLatBounds(
      [pts[0][0], pts[1][1]],
      [pts[1][0], pts[0][1]]
    );
    mapRef.getMap().fitBounds(bounds, { padding: 80, maxZoom: 12 });
  }, [mapRef, frameGeometry]);

  if (loading) {
    return (
      <Tile className={style.loadingTile}>
        <Loading description="Loading map..." withOverlay={false} />
      </Tile>
    );
  }

  if (error || !typeCurveData) {
    return (
      <Tile>
        <div>Failed to load type curve data</div>
      </Tile>
    );
  }

  return (
    <Tile className={style.tile}>
      <div className={style.mapSection}>
        {!hideUnitToolbar && <UnitResolutionToolbar />}
        {showDefineAreaBanner && (
          <InlineNotification
            className={style.defineAreaBanner}
            kind="info"
            lowContrast
            hideCloseButton
            title="Start by defining your Type Curve area."
            subtitle="Select an area of interest on the map or apply a global map filter to bring wells and units into this workspace."
          />
        )}
        <div className={style.mapContainer}>
          <div ref={containerRef} className={style.mapWrapper}>
            {overlappingUnits.length > 0 && (
              <InlineNotification
                className={style.overlapWarning}
                kind="warning"
                lowContrast
                hideCloseButton
                title={`${overlappingUnits.length} unit${
                  overlappingUnits.length === 1 ? ' has' : 's have'
                } major overlaps \u2014 please wait for system to resolve.`}
              />
            )}
            <Map
              className={style.map}
              longitude={longitude}
              latitude={latitude}
              zoom={zoom - 1}
              mapConfigAtom={mapConfigAtom}
              showControls
              showMapFilters
              refAtom={mapRefAtoms[surface]}
              onResetZoom={handleResetZoom}
              scopedLayers={<ScopedWellsLayerSelection />}
              typeCurvesVisible={typeCurveAreaVisible}
              structureGridCollectionId={
                typeCurveData.structureGridCollectionId ?? null
              }
            >
              <TcDevelopmentAreasLayer
                typeCurveGeometry={typeCurveData.geometryAsGeojson}
                typeCurveEwkb={typeCurveData.geometry}
                typeCurveHeaderDetailsId={typeCurveData.id}
              />
              <ScopedWellsLayer typeCurveHeader={typeCurveData} />
              {/* Slot geometries render inside a TC workspace, and also on the
                  Inventory Workspace (which carries `inventoryContext` but not
                  the TC-workspace `effectiveWorkspaceId`). The query is
                  header-scoped, so it works in either context. */}
              {effectiveWorkspaceId || inventoryContext ? (
                <TcSlotGeometriesLayer
                  typeCurveHeaderDetailsId={typeCurveData.id}
                />
              ) : null}

              {inventoryContext && (
                <TcInventoryAreasLayer areas={inventoryContext.areas} />
              )}

              {displayGeometry && typeCurveAreaVisible && (
                <Source
                  id="type-curve-outline"
                  type="geojson"
                  data={displayGeometry}
                >
                  <Layer
                    id="type-curve-outline-layer"
                    type="line"
                    paint={{
                      'line-color': '#ff0000',
                      'line-width': 2,
                      'line-opacity': 0.8,
                    }}
                  />
                </Source>
              )}

              {filterMode === 'aoi-polygon' && aoiPolygon && aoiLocked && (
                <TcAoiPolygonLayer />
              )}
              {/* Never mount both: each claims map gestures for itself. */}
              {!editingShape &&
                filterMode === 'aoi-polygon' &&
                (!aoiPolygon || !aoiLocked) && (
                  <TcDrawPolygonControl
                    initialPolygon={!aoiLocked ? aoiPolygon : undefined}
                  />
                )}
              {effectiveShapeEdit && (
                <TcUnitShapeEditor {...effectiveShapeEdit} />
              )}
              <TcAreaPreviewLayer />
              {extraLayers}
            </Map>
            {/* Harness only — omitted when a shell supplies its own controls. */}
            {!unitShapeEdit && effectiveShapeEdit && (
              <TcUnitShapeEditControls
                canUndo={localEdit.canUndo}
                onUndo={localEdit.undo}
                snap={snap}
                onSnapChange={setSnap}
              />
            )}
            {(wellsLoading || unitActionPending || areaPreview?.loading) && (
              <div className={style.loadingOverlay} aria-busy="true">
                <Loading
                  description={
                    areaPreview?.loading
                      ? 'Loading area...'
                      : unitActionPending
                        ? 'Updating\u2026'
                        : 'Loading wells...'
                  }
                  withOverlay={false}
                />
              </div>
            )}
            <OverlapOnboardingModal />
          </div>
        </div>
      </div>
    </Tile>
  );
};

export default TypeCurveMap;
